import { extractErrorMessages } from '@fukuri-admins/ui/common/Form/logics/extractErrorMessages';
import { ComponentProps } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { DatePickerField } from '..';

type DatePickerFieldProps = ComponentProps<typeof DatePickerField>;

type Props = {
  name: string;
  datePicker?: Omit<
    NonNullable<DatePickerFieldProps['datePicker']>,
    'value' | 'onChange' | 'onBlur'
  >;
  fieldWrapperProps: Omit<DatePickerFieldProps['fieldWrapperProps'], 'htmlFor' | 'errorMessages'>;
};

/**
 * DatePickerField の RHF Controller ラッパー。
 * `Date | null` ↔ DatePicker の `Date | undefined` の変換を内部で吸収する。
 * 利用には FormProvider の配下に置くこと。
 *
 * @param name - RHF フィールド名
 * @param datePicker - DatePicker に渡す追加 props（value・onChange は自動接続されるため不要）
 * @param fieldWrapperProps - FieldWrapper に渡す props（htmlFor・errorMessages は自動設定）
 */
export const DatePickerFieldController: React.FC<Props> = ({
  name,
  fieldWrapperProps,
  datePicker,
}) => {
  const { control } = useFormContext();

  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({ control, name });

  return (
    <DatePickerField
      fieldWrapperProps={{
        htmlFor: name,
        errorMessages: extractErrorMessages(error),
        ...fieldWrapperProps,
      }}
      datePicker={{
        ...datePicker,
        value: value ?? undefined,
        onChange: (date: Date | undefined) => onChange(date ?? null),
        onBlur,
      }}
    />
  );
};
